import * as React from "react"

import { cn } from "@/lib/utils"

export interface InputProps extends React.ComponentProps<"input"> {
  /** Renders the input in an error state (red border + ring tint). */
  error?: boolean
  /** Helper / error message rendered below the input. */
  helperText?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, helperText, id, ...props }, ref) => {
    const inputEl = (
      <input
        type={type}
        id={id}
        aria-invalid={error || undefined}
        aria-describedby={
          helperText && id ? `${id}-helper` : undefined
        }
        className={cn(
          "flex h-field w-full rounded-md border border-input bg-background px-3 py-2 text-base transition-colors duration-150 placeholder:text-muted-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          error &&
            "border-destructive focus-visible:ring-destructive/40",
          className,
        )}
        ref={ref}
        {...props}
      />
    )

    if (!helperText) return inputEl

    return (
      <div className="w-full">
        {inputEl}
        <p
          id={id ? `${id}-helper` : undefined}
          className={cn(
            "mt-1.5 text-caption",
            error ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {helperText}
        </p>
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
